import {
  ChangeEvent,
  DragEvent,
  MouseEvent,
  ReactNode,
  useRef,
  useState,
} from "react";
import { Box, Link, Typography } from "@mui/material";
import { makeSxStyles } from "ui";
import ImageCropDialog from "./ImageCropDialog";

const BYTES_PER_MB = 1024 * 1024;

export interface ImageDropZoneConstraints {
  /** Accepted MIME types, e.g. ["image/jpeg", "image/png"]. */
  mimeTypes: string[];
  /** Accepted file extensions (lowercase, no dot), e.g. ["jpg", "jpeg"]. */
  extensions: string[];
  /** Human-readable list of accepted formats, e.g. "JPG, JPEG or PNG". */
  formatLabel: string;
  maxSizeMb: number;
  minWidth: number;
  minHeight: number;
  /** Required width / height ratio, e.g. 3 for 3:1. */
  aspectRatio?: number;
  /** Human-readable ratio, e.g. "3:1". */
  aspectRatioLabel?: string;
}

interface ImageDropZoneProps {
  constraints: ImageDropZoneConstraints;
  /** Secondary lines shown under "Drop an image here, or browse". */
  hint?: ReactNode;
  /** Object/data URL of the currently selected or saved image. */
  previewUrl: string | null;
  /** Name shown next to the preview. */
  fileName?: string | null;
  /** Only called with files that passed client-side validation. */
  onFileSelect: (file: File) => void;
  onRemove: () => void;
  /**
   * An error from outside the component (e.g. the backend rejected the
   * upload). Shown in the drop zone in place of the preview.
   */
  error?: string | null;
  /** Called when the user dismisses `error` by choosing another file. */
  onErrorDismiss?: () => void;
  disabled?: boolean;
}

const readImageDimensions = (file: File) =>
  new Promise<{ width: number; height: number }>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
      URL.revokeObjectURL(url);
    };
    image.onerror = () => {
      reject(new Error("Unreadable image"));
      URL.revokeObjectURL(url);
    };
    image.src = url;
  });

type ValidationResult = { error: string | null; needsCrop?: boolean };

const validateImageFile = async (
  file: File,
  constraints: ImageDropZoneConstraints,
): Promise<ValidationResult> => {
  const formatError = `File format not supported. Use ${constraints.formatLabel}.`;
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  const hasValidType =
    constraints.mimeTypes.includes(file.type) ||
    (!file.type && constraints.extensions.includes(extension));
  if (!hasValidType) return { error: formatError };

  if (file.size > constraints.maxSizeMb * BYTES_PER_MB) {
    return {
      error: `File is too large. Use a file up to ${constraints.maxSizeMb} MB.`,
    };
  }

  try {
    const { width, height } = await readImageDimensions(file);
    if (width < constraints.minWidth || height < constraints.minHeight) {
      return {
        error: `Image is too small. Minimum size is ${constraints.minWidth} × ${constraints.minHeight} px.`,
      };
    }
    // A different ratio isn't an error: the user crops it to fit (ESR-1656).
    const { aspectRatio } = constraints;
    // The API checks width === height * ratio exactly, so even 1201 × 400
    // goes through the crop step, which exports an exact size.
    if (aspectRatio && width !== height * aspectRatio) {
      return { error: null, needsCrop: true };
    }
  } catch {
    return { error: formatError };
  }

  return { error: null };
};

// Drag-and-drop / click-to-browse image picker with client-side format, size
// and minimum-dimension validation, a crop step for images in the wrong
// aspect ratio (ESR-1656), plus a thumbnail + Replace/Remove state
// once an image is selected (ESR-1655).
const ImageDropZone = ({
  constraints,
  hint,
  previewUrl,
  fileName,
  onFileSelect,
  onRemove,
  error: externalError,
  onErrorDismiss,
  disabled,
}: ImageDropZoneProps) => {
  const styles = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [fileToCrop, setFileToCrop] = useState<File | null>(null);

  const error = validationError ?? externalError ?? null;

  const openFilePicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    const { error: fileError, needsCrop } = await validateImageFile(
      file,
      constraints,
    );
    setValidationError(fileError);
    if (needsCrop) {
      setFileToCrop(file);
    } else if (!fileError) {
      onErrorDismiss?.();
      onFileSelect(file);
    }
  };

  const handleCropApply = (croppedFile: File) => {
    setFileToCrop(null);
    onErrorDismiss?.();
    onFileSelect(croppedFile);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    void handleFile(event.target.files?.[0]);
    // Reset so picking the same file again still fires onChange.
    event.target.value = "";
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    // dragleave also fires when moving onto a child (the text or links);
    // only reset once the pointer actually leaves the zone.
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return;
    }
    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    const { files } = event.dataTransfer;
    if (files.length > 1) {
      setValidationError("Drop one image at a time.");
      return;
    }
    void handleFile(files[0]);
  };

  const handleLinkClick = (event: MouseEvent<HTMLButtonElement>) => {
    // The zone itself also opens the picker on click - don't open it twice.
    event.stopPropagation();
    openFilePicker();
  };

  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      hidden
      accept={[
        ...constraints.mimeTypes,
        ...constraints.extensions.map((extension) => `.${extension}`),
      ].join(",")}
      onChange={handleInputChange}
      data-testid="image-drop-zone-input"
    />
  );

  const cropDialog = constraints.aspectRatio ? (
    <ImageCropDialog
      file={fileToCrop}
      aspectRatio={constraints.aspectRatio}
      aspectRatioLabel={constraints.aspectRatioLabel}
      minWidth={constraints.minWidth}
      onApply={handleCropApply}
      onCancel={() => setFileToCrop(null)}
    />
  ) : null;

  // A client-side rejection of a replacement keeps the current image visible
  // (it's still what gets saved) with the error under it. A backend rejection
  // swaps in the error state below.
  if (previewUrl && !externalError) {
    return (
      <>
        {cropDialog}
        <Box sx={styles.preview}>
          {fileInput}
          <Box
            component="img"
            src={previewUrl}
            alt={fileName ?? "Selected image"}
            sx={styles.thumbnail}
          />
          <Box sx={styles.previewInfo}>
            <Typography sx={styles.fileName} title={fileName ?? undefined}>
              {fileName}
            </Typography>
            {validationError && (
              <Typography sx={styles.errorText}>{validationError}</Typography>
            )}
            <Box sx={styles.previewActions}>
              <Link
                component="button"
                type="button"
                underline="none"
                sx={styles.primaryAction}
                onClick={openFilePicker}
                disabled={disabled}
              >
                Replace
              </Link>
              <Link
                component="button"
                type="button"
                underline="none"
                sx={styles.secondaryAction}
                onClick={() => {
                  setValidationError(null);
                  onRemove();
                }}
                disabled={disabled}
              >
                Remove
              </Link>
            </Box>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      {cropDialog}
      {/* Not a button itself: it holds real buttons (browse, Choose another
          file, Discard), and nested controls get skipped by screen readers
          or double-activated. Keyboard users get the picker through those
          links; clicking anywhere in the zone is a mouse shortcut. */}
      <Box
        sx={styles.zone}
        data-error={Boolean(error)}
        data-dragging={isDragging}
        onClick={openFilePicker}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-testid="image-drop-zone"
      >
        {fileInput}
        {error ? (
          <>
            <Typography sx={styles.errorText}>{error}</Typography>
            <Link
              component="button"
              type="button"
              underline="none"
              sx={styles.chooseAnother}
              onClick={handleLinkClick}
              disabled={disabled}
            >
              Choose another file
            </Link>
            {/* A backend rejection blocks saving until the user picks another
              file or discards the rejected one, so nothing is saved without
              the user noticing. Discarding falls back to the saved image on
              Edit, or no image on Add. */}
            {externalError && !validationError && onErrorDismiss && (
              <Link
                component="button"
                type="button"
                underline="none"
                sx={styles.secondaryAction}
                onClick={(event: MouseEvent<HTMLButtonElement>) => {
                  event.stopPropagation();
                  onErrorDismiss();
                }}
                disabled={disabled}
              >
                Discard this file
              </Link>
            )}
          </>
        ) : (
          <>
            <Typography sx={styles.title}>
              Drop an image here, or{" "}
              <Link
                component="button"
                type="button"
                underline="none"
                sx={styles.browse}
                onClick={handleLinkClick}
                disabled={disabled}
              >
                browse
              </Link>
            </Typography>
            {hint && <Box sx={styles.hint}>{hint}</Box>}
          </>
        )}
      </Box>
    </>
  );
};

const linkText = {
  fontSize: "14px",
  lineHeight: "20px",
  verticalAlign: "baseline",
};

const useStyles = makeSxStyles((theme) => ({
  zone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    minHeight: "112px",
    px: "20px",
    py: "16px",
    border: "1px dashed",
    borderColor: theme.palette.grey[400],
    borderRadius: "8px",
    textAlign: "center",
    cursor: "pointer",
    transition: "border-color 120ms ease, background-color 120ms ease",
    "&:hover": {
      borderColor: theme.palette.grey[300],
    },
    '&[data-error="true"]': {
      borderColor: theme.palette.error.main,
    },
    '&[data-dragging="true"]': {
      borderColor: theme.palette.primary.main,
      backgroundColor: "rgba(138, 206, 255, 0.08)",
    },
  },
  title: {
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.palette.text.primary,
  },
  browse: {
    ...linkText,
    color: theme.palette.primary.main,
  },
  hint: {
    fontSize: "12px",
    lineHeight: "16px",
    color: "#c1c7ce",
  },
  errorText: {
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.palette.error.main,
  },
  chooseAnother: {
    ...linkText,
    color: theme.palette.primary.main,
  },
  preview: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
  },
  thumbnail: {
    // Shrinks in narrow form columns so the file name keeps some room.
    flex: "0 1 240px",
    minWidth: "160px",
    aspectRatio: "3 / 1",
    objectFit: "cover",
    borderRadius: "8px",
    display: "block",
  },
  previewInfo: {
    flex: "1 1 0",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    minWidth: "140px",
  },
  fileName: {
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.palette.text.primary,
    // Long names wrap onto a second line before being clipped.
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    // Wrap at spaces first; only split inside a word (long file names) when
    // it doesn't fit on a line.
    overflowWrap: "anywhere",
  },
  previewActions: {
    display: "flex",
    gap: "20px",
  },
  primaryAction: {
    ...linkText,
    color: theme.palette.primary.main,
  },
  secondaryAction: {
    ...linkText,
    color: theme.palette.text.primary,
  },
}));

export default ImageDropZone;
